import React from 'react';
import { Input } from '@/components/ui/input';
import type { DiscrepancyItem } from '@/types/discrepancy-review.types';

interface Props {
  items: DiscrepancyItem[];
  editedQuantities: Record<string, number>;
  onQuantityChange: (id: string, quantity: number) => void;
  loading: boolean;
  hasSearched: boolean;
}

export const DiscrepancyTable: React.FC<Props> = ({
  items, editedQuantities, onQuantityChange, loading, hasSearched
}) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center text-gray-400">
        Buscando diferencias...
      </div>
    );
  }

  if (!hasSearched) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center text-gray-400">
        Selecciona los filtros y presiona Buscar
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-12 text-center text-gray-400">
        No se encontraron registros con los filtros seleccionados
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                Insumo
              </th>
              <th className="py-3 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                Categoría
              </th>
              <th className="py-3 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                Referencia
              </th>
              <th className="py-3 px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[60px]">
                Und
              </th>
              <th className="py-3 px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                Cant. Pedida
              </th>
              <th className="py-3 px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                Cant. Recibida
              </th>
              <th className="py-3 px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                Diferencia
              </th>
              <th className="py-3 px-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                Cant. Confirmada
              </th>
              <th className="py-3 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px]">
                Fecha
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map(item => {
              const hasDiff = Math.abs(item.difference) > 0.001;
              const isEdited = editedQuantities[item.id] !== undefined;
              const confirmedValue = isEdited ? editedQuantities[item.id] : item.receivedQuantity;
              const adjustmentDelta = isEdited ? confirmedValue - item.receivedQuantity : 0;
              const hasAdjustment = Math.abs(adjustmentDelta) > 0.001;

              return (
                <tr
                  key={item.id}
                  className={`hover:bg-gray-50/50 ${hasDiff ? 'bg-amber-50/40' : ''}`}
                >
                  <td className="py-2.5 px-4 font-medium text-gray-900 text-sm">
                    {item.ingredientName}
                  </td>
                  <td className="py-2.5 px-3 text-gray-500 text-sm capitalize">
                    {item.categoryName}
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                        item.sourceType === 'TRANSFER'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {item.sourceType === 'TRANSFER' ? 'TRF' : 'O/C'}
                      </span>
                      <span className="text-sm text-gray-600">{item.sourceNumber}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-center text-gray-400 text-xs">
                    {item.unit}
                  </td>
                  <td className="py-2.5 px-3 text-center text-sm text-gray-700">
                    {item.orderedQuantity}
                  </td>
                  <td className="py-2.5 px-3 text-center text-sm text-gray-700">
                    {item.receivedQuantity}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    {hasDiff ? (
                      <span className={`text-sm font-semibold ${
                        item.difference > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.difference > 0 ? '+' : ''}{item.difference.toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">0</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <Input
                      type="number"
                      value={confirmedValue}
                      onChange={e => onQuantityChange(item.id, parseFloat(e.target.value) || 0)}
                      className={`w-24 mx-auto text-center text-sm h-8 ${
                        hasAdjustment
                          ? 'bg-amber-50 border-amber-300 ring-1 ring-amber-200'
                          : ''
                      }`}
                      min="0"
                      step="0.001"
                    />
                  </td>
                  <td className="py-2.5 px-3 text-sm text-gray-500">
                    {item.receivedAt
                      ? new Date(item.receivedAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'short' })
                      : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-3 border-t text-sm text-gray-500">
        {items.length} registro{items.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};
